import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Users2 } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import FaceLibraryBrowser from "./FaceLibraryBrowser";
import FaceGenerationPanel from "./FaceGenerationPanel";
import { toast } from "sonner";

/** One face library: health at a glance, generation with visible progress,
 *  and a single browsable list with four states. */
const AdminFaces = () => {
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState(null);
  const [openRegion, setOpenRegion] = useState("uk");
  const [focusState, setFocusState] = useState("live");
  const [target, setTarget] = useState(40);

  const loadStats = useCallback(async () => {
    const res = await authFetch(`${API}/admin/faces/stats`);
    if (res.ok) {
      const data = await res.json();
      setStats(data);
      if (data.targets?.default != null) setTarget(data.targets.default);
    }
  }, []);

  const loadOptions = useCallback(async () => {
    const res = await authFetch(`${API}/admin/faces/regions`);
    if (res.ok) setOptions(await res.json());
  }, []);

  useEffect(() => {
    loadStats();
    loadOptions();
  }, [loadStats, loadOptions]);

  const saveTarget = async () => {
    const res = await authFetch(`${API}/admin/faces/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ default: Number(target) }),
    });
    if (res.ok) {
      toast.success(`Target set to ${target} live faces a region`);
      loadStats();
    } else {
      toast.error("Could not save the target");
    }
  };

  const syncLibrary = async () => {
    if (!window.confirm("Re-run manifest seeding for this environment? It adds any missing shipped faces and changes nothing else.")) return;
    const res = await authFetch(`${API}/admin/faces/seed`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast.success(
        data.inserted ? `Added ${data.inserted} faces from the manifest` : "Library already in sync"
      );
      loadStats();
    } else {
      toast.error("Sync failed");
    }
  };

  const regionLabel = (key) => options?.regions?.find((r) => r.key === key)?.label || key;
  const below = (stats?.by_region || []).filter((r) => r.shortfall > 0);

  const reviewRegion = (region) => {
    if (region) setOpenRegion(region);
    setFocusState("pending");
  };

  return (
    <Card className="bg-card border-border" data-testid="admin-faces">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users2 className="w-5 h-5 text-primary" />
          Face Library
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Library health at a glance */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="face-stats">
            <div className="p-3 bg-muted border border-border">
              <div className="text-2xl font-bold" data-testid="face-stat-approved">
                {stats.approved}
              </div>
              <p className="text-xs text-muted-foreground">Live — served to trainees</p>
            </div>
            <button
              onClick={() => reviewRegion(null)}
              className="p-3 bg-muted border border-border text-left hover:border-primary/60 transition-colors"
              data-testid="face-stat-pending-card"
            >
              <div className="text-2xl font-bold" data-testid="face-stat-pending">
                {stats.pending}
              </div>
              <p className="text-xs text-muted-foreground">Awaiting review</p>
            </button>
            <div className="p-3 bg-muted border border-border">
              <div className="text-2xl font-bold" data-testid="face-stat-below-target">
                {below.length}
              </div>
              <p className="text-xs text-muted-foreground">
                {below.length === 0
                  ? "Every region at target"
                  : `Below target: ${below.map((r) => r.label).join(", ")}`}
              </p>
            </div>
            <div className="p-3 bg-muted border border-border">
              <div className="text-2xl font-bold">{stats.retired + stats.rejected}</div>
              <p className="text-xs text-muted-foreground">Retired / rejected</p>
            </div>
          </div>
        )}

        <FaceGenerationPanel
          options={options}
          onChanged={loadStats}
          onReview={reviewRegion}
        />

        {/* Where the library is thin */}
        {stats?.by_region && (
          <div className="mb-6" data-testid="face-region-breakdown">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
              <Label className="text-sm">Live faces by region · click a region to browse it</Label>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">
                    Target live faces per region
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    max={500}
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    className="w-24 h-9 bg-background"
                    data-testid="face-target-input"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={saveTarget} data-testid="face-target-save">
                  Save target
                </Button>
                <Button size="sm" variant="outline" onClick={syncLibrary} data-testid="face-sync-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync library
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {stats.by_region.map((r) => (
                <button
                  key={r.region}
                  onClick={() => setOpenRegion(r.region)}
                  className={`p-2 border text-left transition-colors ${
                    openRegion === r.region
                      ? "bg-primary/10 border-primary"
                      : "bg-muted border-border hover:border-primary/60"
                  }`}
                  data-testid={`face-region-stat-${r.region}`}
                >
                  <p className="text-xs font-semibold">{r.label}</p>
                  <p className="text-xs text-muted-foreground">
                    Simple{" "}
                    <span className={r.thin_simple ? "text-amber-400 font-bold" : "text-foreground"}>
                      {r.simple}
                    </span>{" "}
                    · Complex{" "}
                    <span className={r.thin_complex ? "text-amber-400 font-bold" : "text-foreground"}>
                      {r.complex}
                    </span>
                    {r.unclassified ? ` · +${r.unclassified} earlier` : ""}
                  </p>
                  <p
                    className={`text-xs ${r.shortfall ? "text-amber-400" : "text-muted-foreground"}`}
                    data-testid={`face-region-shortfall-${r.region}`}
                  >
                    {r.total} live of {r.target}
                    {r.shortfall ? ` · need ${r.shortfall}` : " · at target"}
                  </p>
                </button>
              ))}
            </div>
            {stats.lookalike_flagged > 0 && (
              <p className="text-xs text-amber-400 mt-2" data-testid="face-lookalike-summary">
                {stats.lookalike_flagged} face
                {stats.lookalike_flagged === 1 ? "" : "s"} flagged as a possible look-alike of
                someone already in the library.
              </p>
            )}
          </div>
        )}

        <FaceLibraryBrowser
          region={openRegion}
          regionLabel={regionLabel(openRegion)}
          focusState={focusState}
          onChanged={loadStats}
        />
      </CardContent>
    </Card>
  );
};

export default AdminFaces;
